import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Contrat du rig partagé : interdit. Les particules sont la seule lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'magneto',
  family: 'particles',
  danceability: 0.78,
  energyLevel: 0.72,
  bpmMin: 110,
  bpmMax: 145,
  movement: 0.75,
  tonality: 0.75,
  prefers: { dynamics: [0.25, 1.0] },
  enabled: true
};

// Magneto : des dizaines de milliers de particules lumineuses qui suivent les
// lignes de champ de 2 à 4 dipôles magnétiques en orbite, dans le noir. Des
// boucles d'aurore en 3D qui respirent avec la musique (Magnetosphere, 2008).
//
// LE DÉTAIL DÉCISIF : les lignes de champ sont ANALYTIQUES. Autour d'un
// dipôle, en sphériques, r = L sin²θ. Chaque particule porte, une fois pour
// toutes, un dipôle, une ligne (L, azimut φ), une phase et une vitesse ; le
// vertex shader fait avancer θ avec le temps, calcule r, tourne par l'axe du
// dipôle et translate par sa position - tout en uniformes. Aucune
// simulation, aucun upload par frame : la seule chose que le CPU envoie est
// la poignée d'uniformes des dipôles.
//
// Un rayon de coeur r0 est ajouté à r : la ligne entre dans le dipôle à ±r0
// sur son axe, et ces deux points sont les PÔLES, des points de convergence
// distincts (avec r = L sin²θ pur, nord et sud se confondent au centre).
// Le temps le long de la ligne est compressé près des pôles (dθ/ds =
// 1 + k cos 2πs) : les particules y vont vite, comme dans le vrai champ.
//
// LA RECONNEXION (drop) : chaque particule porte DEUX assignations (dipôle,
// L, φ). Un drop bascule l'assignation courante et fait courir une
// progression 0 → 1 en deux temps ; la position est l'interpolation des deux
// lignes - continue par construction, sans saut.

const COUNT = { 0: 15000, 1: 30000, 2: 60000 };
const SPRITE_BASE = { 0: 1.6, 1: 1.25, 2: 1.0 };   // les sprites sont plus grands au tier 0
const MAX_DIPOLES = 4;
const CORE_R = 0.12;                  // les pôles, à ±r0 sur l'axe
const L_LEVELS = [0.45, 0.62, 0.78, 0.9, 1.0];   // cinq boucles par azimut, relatives à la force du dipôle
const L_SCALE = 1.5;
const FORCE_MIN = 0.9, FORCE_MAX = 1.6;
const POLE_SPEED_K = 0.6;             // compression du temps près des pôles
const FLOW_BASE = 0.07;               // tours de ligne par seconde
const BREATH_GAIN = 0.08, BREATH_S = 0.15;
const RECONNECT_BEATS = 2;
const FLASH_S = 0.4;
const ORBIT_BARS = 32;                // la caméra : un tour en 32 mesures
const ORBIT_R = [1.3, 2.3];           // les dipôles orbitent entre ces rayons (jamais au centre, sous la pochette)
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;
const PAUSE_FLOW = 0.05;
const SAFE_FLOOR = 0.45;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const vertexShader = `
  precision highp float;
  attribute vec4 aLine;      // dipôle A, L, phi, phase
  attribute vec4 aLine2;     // dipôle B, L2, phi2, vitesse propre
  attribute float aSeed;
  uniform vec3 uDipPos[${MAX_DIPOLES}];
  uniform vec3 uDipAxis[${MAX_DIPOLES}];
  uniform float uDipForce[${MAX_DIPOLES}];
  uniform float uDipFlow[${MAX_DIPOLES}];   // le temps d'écoulement intégré, par dipôle (signe = polarité)
  uniform vec3 uDipCol[${MAX_DIPOLES}];
  uniform float uDipCount;
  uniform float uUseB;        // l'assignation courante : 0 = A, 1 = B
  uniform float uReconnect;   // 0 -> 1 : de l'autre assignation vers la courante
  uniform float uBreath;
  uniform float uTwinkle;
  uniform float uTime;
  uniform float uSprite;
  uniform float uPixelRatio;
  uniform float uLinesOnly;
  uniform float uFlash;
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  varying vec3 vColor;
  varying float vAtt;

  float safeAtt(vec4 clipPos) {
    vec2 ndc = clipPos.xy / max(clipPos.w, 0.0001);
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return mix(${SAFE_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
  }
  // un repère orthonormé autour d'un axe
  void frame(vec3 axis, out vec3 u, out vec3 v) {
    vec3 h = abs(axis.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    u = normalize(cross(axis, h));
    v = cross(axis, u);
  }
  // La position d'une particule sur une ligne de champ d'un dipôle.
  // s : la phase le long de la ligne (0 = pôle nord, 0,5 = pôle sud, 1 = retour)
  vec3 onLine(int i, float L, float phi, float s, float seed, out float poleness) {
    // le temps compressé près des pôles : dθ/ds = 1 + k cos(2πs)
    float th = 6.2831853 * (s + ${POLE_SPEED_K.toFixed(2)} * sin(6.2831853 * s) / 6.2831853);
    float sn = sin(th), cs = cos(th);
    float r = (L * uDipForce[i] * uBreath) * sn * sn + ${CORE_R.toFixed(3)};
    // une légère dispersion transverse, analytique
    float w = 0.012 * L * (sin(th * 3.0 + seed * 6.2831853) + 0.5 * sin(th * 7.0 - seed * 9.0));
    vec3 u, v; frame(uDipAxis[i], u, v);
    vec3 radial = u * cos(phi) + v * sin(phi);
    vec3 side = u * cos(phi + 1.5708) + v * sin(phi + 1.5708);
    poleness = pow(abs(cs), 6.0);
    return uDipPos[i] + uDipAxis[i] * (r * cs) + radial * (r * sn) + side * w;
  }

  void main() {
    int ia = int(mod(aLine.x, uDipCount));
    int ib = int(mod(aLine2.x, uDipCount));
    float speed = aLine2.w;
    float pa = aLine.w, pb = fract(aLine.w + 0.37 * aSeed);
    float pA = 0.0, pB = 0.0;
    vec3 posA = onLine(ia, aLine.y, aLine.z, fract(pa + uDipFlow[ia] * speed), aSeed, pA);
    vec3 posB = onLine(ib, aLine2.y, aLine2.z, fract(pb + uDipFlow[ib] * speed), aSeed, pB);
    // la reconnexion : de l'autre assignation vers la courante, lissée
    float k = smoothstep(0.0, 1.0, uReconnect);
    float toB = mix(1.0 - k, k, uUseB);
    vec3 pos = mix(posA, posB, toB);
    float poleness = mix(pA, pB, toB);
    vec3 col = mix(uDipCol[ia], uDipCol[ib], toB);
    // les pôles : des points de convergence au coeur blanc
    col = mix(col, vec3(1.0), poleness * 0.85 + uFlash * 0.6);
    float bright = 0.55 + 1.6 * poleness + uFlash * 0.8;
    // le scintillement
    bright *= 1.0 + uTwinkle * 0.6 * sin(uTime * 9.0 + aSeed * 50.0);
    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    vec4 clip = projectionMatrix * mv;
    gl_Position = clip;
    vAtt = safeAtt(clip);
    // profondeur de champ simulée par la taille : 2 à 6 px selon la distance
    float dist = max(-mv.z, 0.3);
    float px = clamp(26.0 / dist, 2.0, 6.0) * uSprite * uPixelRatio;
    if (uLinesOnly > 0.5 && fract(aSeed * 50.0) > 0.02) px = 0.0;
    gl_PointSize = px;
    vColor = col * bright * (6.0 / max(px / (uSprite * uPixelRatio), 2.0));
  }
`;

const fragmentShader = `
  precision mediump float;
  varying vec3 vColor;
  varying float vAtt;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float g = exp(-dot(d, d) * 14.0);
    gl_FragColor = vec4(vColor * g * vAtt * 0.05, 1.0);
  }
`;

export class MagnetoVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._count = COUNT[this._tier] || COUNT[1];
    const nd = parseInt(urlParam('magdipoles'), 10);
    this._forceDipoles = Number.isFinite(nd) ? Math.max(1, Math.min(MAX_DIPOLES, nd)) : null;
    const rc = parseFloat(urlParam('magreconnect'));
    this._forceReconnect = Number.isFinite(rc) ? clamp01(rc) : null;
    this._linesOnly = urlParam('maglines') === '1';
    this._postProcessing = { bloomStrength: 0.5, bloomThreshold: 0.7 };
  }

  get postProcessing() { return this._postProcessing; }

  _random() { this._seed = (this._seed * 1664525 + 1013904223) >>> 0; return this._seed / 4294967296; }

  init(scene, camera) {
    this._scene = scene; this._camera = camera;
    this._seed = 17;
    this._time = 0;
    const n = this._count;
    const line = new Float32Array(n * 4), line2 = new Float32Array(n * 4), seed = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const dA = Math.floor(this._random() * MAX_DIPOLES);
      line[i * 4] = dA;
      // LES LIGNES SONT DISCRÈTES : L sur 10 niveaux, φ sur 32 azimuts (plus un
      // soupçon de jeu). Des lignes tirées au hasard remplissent un volume
      // uniforme, un brouillard ; des lignes partagées par 180 particules
      // chacune se lisent comme des fils, et la densité additive en fait des voiles.
      const qL = () => L_LEVELS[Math.min(L_LEVELS.length - 1, Math.floor(Math.pow(this._random(), 0.6) * L_LEVELS.length))] * L_SCALE * (1 + (this._random() - 0.5) * 0.03);
      const qPhi = () => (Math.floor(this._random() * 20) + 0.5) / 20 * Math.PI * 2 + (this._random() - 0.5) * 0.03;
      line[i * 4 + 1] = qL();
      line[i * 4 + 2] = qPhi();
      line[i * 4 + 3] = this._random();
      line2[i * 4] = (dA + 1 + Math.floor(this._random() * (MAX_DIPOLES - 1))) % MAX_DIPOLES;
      line2[i * 4 + 1] = qL();
      line2[i * 4 + 2] = qPhi();
      line2[i * 4 + 3] = 0.8 + 0.4 * this._random();
      seed[i] = this._random();
    }
    this._geo = new THREE.BufferGeometry();
    this._geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));   // inutilisé : tout vient de aLine
    this._geo.setAttribute('aLine', new THREE.BufferAttribute(line, 4));
    this._geo.setAttribute('aLine2', new THREE.BufferAttribute(line2, 4));
    this._geo.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    this._geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 6);
    this._u = {
      uDipPos: { value: [0, 1, 2, 3].map(() => new THREE.Vector3()) },
      uDipAxis: { value: [0, 1, 2, 3].map(() => new THREE.Vector3(0, 1, 0)) },
      uDipForce: { value: [1, 1, 1, 1] },
      uDipFlow: { value: [0, 0, 0, 0] },
      uDipCol: { value: [0, 1, 2, 3].map(() => new THREE.Color(0.5, 0.7, 1)) },
      uDipCount: { value: 3 },
      uUseB: { value: 0 }, uReconnect: { value: 1 },
      uBreath: { value: 1 }, uTwinkle: { value: 0.3 }, uTime: { value: 0 },
      uSprite: { value: SPRITE_BASE[this._tier] || 1 }, uPixelRatio: { value: 1 },
      uLinesOnly: { value: this._linesOnly ? 1 : 0 }, uFlash: { value: 0 },
      uAspect: safeZoneUniforms.uAspect, uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize, uSafeFeather: safeZoneUniforms.uSafeFeather
    };
    this._mat = new THREE.ShaderMaterial({
      uniforms: this._u, vertexShader, fragmentShader,
      transparent: true, depthTest: false, depthWrite: false, blending: THREE.AdditiveBlending
    });
    this._points = new THREE.Points(this._geo, this._mat);
    this._points.frustumCulled = false;
    const self = this;
    this._points.onBeforeRender = function (renderer) { renderer.getDrawingBufferSize(_size); self._u.uPixelRatio.value = Math.max(0.6, _size.y / 720); };
    scene.add(this._points);

    // les dipôles
    this._dipoles = [];
    for (let i = 0; i < MAX_DIPOLES; i++) {
      this._dipoles.push({
        orbitR: lerp(ORBIT_R[0], ORBIT_R[1], this._random()), orbitA: this._random() * Math.PI * 2, orbitSpeed: (0.05 + 0.05 * this._random()) * (this._random() < 0.5 ? 1 : -1),
        orbitTilt: (this._random() - 0.5) * 0.9,
        axisA: this._random() * Math.PI * 2, axisB: (this._random() - 0.5) * 1.2, precess: 0.15 + 0.2 * this._random(),
        force: lerp(FORCE_MIN, FORCE_MAX, this._random()), flow: this._random() * 10, flip: 1, flipUntil: -1,
        targetR: 0
      });
      this._dipoles[i].targetR = this._dipoles[i].orbitR;
    }
    this._dipCount = this._forceDipoles || 3;
    this._buildup = 0;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._free = 0; this._beats = 0; this._secondsBeat = 0.5;
    this._low = 0.5; this._mid = 0.4; this._high = 0.3;
    this._breathAge = 10; this._reconnectT = 1; this._useB = 0; this._flashUntil = -1;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null;
    this._paletteSeen = undefined;
    this._paused = false;
    this._camAngle = 0;
  }

  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    if (pal === this._paletteSeen) return;
    this._paletteSeen = pal;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0x4fd0ff, 0xff5fb0, 0x9b6bff, 0x5bffb5];
    const hsl = { h: 0, s: 0, l: 0 };
    for (let i = 0; i < MAX_DIPOLES; i++) {
      const c = new THREE.Color(v[i % v.length]); c.getHSL(hsl);
      this._u.uDipCol.value[i].setHSL(hsl.h, Math.max(0.6, hsl.s), 0.6);
    }
  }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._dropIdx = 0; this._lastNowMs = null; }

  _reconnect() {
    this._useB = 1 - this._useB;
    this._reconnectT = 0;
    this._flashUntil = this._time + FLASH_S;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    // pause : l'écoulement ralenti à 5 %
    const dt = Math.min(deltaTime, 0.1) * (this._paused ? PAUSE_FLOW : 1);
    this._time += dt;
    this._applyPalette(profile);
    const director = profile.director;
    const bands = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    if (bands) { this._low = clamp01(bands.low); this._mid = clamp01(bands.mid); this._high = clamp01(bands.high); }
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / bpm : 0.5;
    const barS = 4 * this._secondsBeat;

    let beat = false, bar = false, phrase = false;
    if (!this._paused) {
      const bp = profile.beatPhase;
      if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; }
      else { this._free += dt; if (this._free >= this._secondsBeat) { this._free -= this._secondsBeat; beat = true; } }
      const barPhase = profile.barPhase;
      if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
      else if (beat && (this._beats + 1) % 4 === 0) bar = true;
      const ph = profile.phrasePhase;
      if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
      else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
      if (beat) this._beats++;
    }
    // la montée : un drop connu qui approche, ou le segment « build » de l'analyse
    const next = director && director.nextEvent;
    let build = 0;
    if (next && next.type === 'drop' && next.msUntil < 8000) build = 1 - next.msUntil / 8000;
    if (director && director.mode === 'build') build = Math.max(build, 0.6);
    this._buildup += (build - this._buildup) * (1 - Math.exp(-dt / 1.5));

    if (beat) this._breathAge = 0;
    this._breathAge += dt;
    const breath = this._breathAge < BREATH_S ? Math.sin(Math.PI * this._breathAge / BREATH_S) : 0;
    // MESURE : un dipôle inverse sa polarité pendant un temps
    if (bar) { const d = this._dipoles[Math.floor(this._random() * this._dipCount)]; d.flip = -1; d.flipUntil = this._time + this._secondsBeat; }
    // PHRASE : nombre de dipôles et orbites changent
    if (phrase) {
      if (!this._forceDipoles) this._dipCount = 2 + Math.floor(this._random() * 3);
      for (const d of this._dipoles) { d.targetR = lerp(ORBIT_R[0], ORBIT_R[1], this._random()); d.orbitSpeed = (0.05 + 0.05 * this._random()) * (this._random() < 0.5 ? 1 : -1); }
    }
    // DROP : la reconnexion
    if (!this._paused && this._tickDrops(profile)) this._reconnect();
    if (this._reconnectT < 1) this._reconnectT = Math.min(1, this._reconnectT + dt / (RECONNECT_BEATS * this._secondsBeat));

    // les dipôles : orbite lente, précession, force par bands.low, montée vers le centre
    const U = this._u;
    for (let i = 0; i < MAX_DIPOLES; i++) {
      const d = this._dipoles[i];
      if (this._time > d.flipUntil) d.flip = 1;
      d.orbitR += (d.targetR - d.orbitR) * (1 - Math.exp(-dt / 2.5));
      d.orbitA += d.orbitSpeed * dt;
      d.axisA += d.precess * dt * 0.5;
      // bands.mid : la vitesse d'écoulement ; la polarité inverse le signe
      d.flow += dt * FLOW_BASE * (0.6 + 0.9 * this._mid) * d.flip;
      const R = d.orbitR * (1 - 0.5 * this._buildup);
      U.uDipPos.value[i].set(Math.cos(d.orbitA) * R, Math.sin(d.orbitA * 0.7 + i) * 0.5 * R * d.orbitTilt, Math.sin(d.orbitA) * R);
      U.uDipAxis.value[i].set(Math.cos(d.axisA) * Math.cos(d.axisB), Math.sin(d.axisB), Math.sin(d.axisA) * Math.cos(d.axisB)).normalize();
      U.uDipForce.value[i] = d.force * (0.8 + 0.5 * this._low);
      U.uDipFlow.value[i] = d.flow;
    }
    U.uDipCount.value = this._dipCount;
    U.uUseB.value = this._useB;
    U.uReconnect.value = this._forceReconnect != null ? this._forceReconnect : this._reconnectT;
    U.uBreath.value = 1 + BREATH_GAIN * breath;
    U.uTwinkle.value = this._high;
    U.uTime.value = this._time;
    U.uFlash.value = this._time < this._flashUntil ? (this._flashUntil - this._time) / FLASH_S : 0;

    // la caméra : un tour en 32 mesures, légère variation d'altitude
    this._camAngle += dt * 2 * Math.PI / (ORBIT_BARS * barS);
    const cam = this._camera, a = this._camAngle;
    cam.position.set(Math.sin(a) * 5.0, 0.8 + 0.8 * Math.sin(a * 0.5), Math.cos(a) * 5.0);
    cam.lookAt(0, 0, 0);
  }

  dispose() {
    if (this._scene && this._points) { this._points.onBeforeRender = null; this._scene.remove(this._points); }
    if (this._geo) this._geo.dispose();
    if (this._mat) this._mat.dispose();
  }
}

const _size = new THREE.Vector2();
